import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import Chart from './chart';
import { object } from 'prop-types';
import globalVPS from 'js/global-vps';
import { getSelectStyles } from './selectStyles';
import Disclaimer from '../chart-utils/disclaimer/component';
import Legend from '../chart-utils/legend/component';
import RangeSlider from './RangeSlider';
import { SelectionContext } from './context';
import exportElementAsImage from '../../utils/exportElementAsImage';
import CheckboxFilters from './checkboxFilters';
import { Toggle } from './toggle/index';
import DownloadButton from '../chart-utils/download-button/component';

const selection = ( { pageWrapper, componentWrapper, attributes } ) => {

    const { chart, endpoint, demographics, defaultEconomicVariable, richTextHeading, numFilesToFetch } = attributes;
    const { chartColors, disclaimer } = chart;
    const allDemographicFilterOptions = Object.keys( demographics ).map( ( d ) => {

        return { label: d, value: d };

    } );

    const multiCheckboxDemographicFilterOptions = Object.keys( demographics )

        .map( ( d ) => {

            return {
                label: d,
                options: demographics[ d ].map( ( o ) => {

                    return { label: o, value: o };

                } ),
            };

        } );

    const [ chartTitle, setChartTitle ] = useState( '' );
    const [ chartSubtitle, setActiveChartSubtitle ] = useState( '' );
    const [ toggleVisible, setToggleVisible ] = useState( false );
    const [ toggle, setToggle ] = useState( false );
    const [ renderChart, setRenderChart ] = useState( false );
    const [ allChartData, setAllChartData ] = useState( {} );
    const [ filterCategoryMap, setFilterCategoryMap ] = useState( '' );
    const [ econVariableFilterOptions, setEconVariableFilterOptions ] = useState( [] );
    const [ selectedEconVariableFilter, setSelectedEconVariableFilter ] = useState( '' );
    const [ selectedDemographicFilter, setSelectedDemographicFilter ] = useState( allDemographicFilterOptions[ 0 ] );
    const [ selectedCheckboxFilters, setSelectedCheckboxFilters ] = useState( [] );
    const [ availableCheckboxFilters, setAvailableCheckboxFilters ] = useState( {} );
    const [ slimmedEconData, setSlimmedEconData ] = useState( [] );
    const [ slimmedEconDemographicData, setSlimmedEconDemographicData ] = useState( [] );
    const [ slimmedCheckboxFilteredData, setSlimmedCheckboxFilteredData ] = useState( [] );
    const [ allQuarters, setAllQuarters ] = useState( [] );
    const [ titleHeights, setTitleHeights ] = useState( 0 );
    const [ legendLabels, setLegendLabels ] = useState( [] );
    const [ dynamicDisclaimer, setDynamicDisclaimer ] = useState( disclaimer );

    const [ width, setWidth ] = useState( componentWrapper.offsetWidth < 768
        ? componentWrapper.offsetWidth - 30
        : componentWrapper.offsetWidth - 60 );
    const [ formattedDatasets, setFormattedDatasets ] = useState( [] );
    const [ selectionQuarters, setSelectionQuarters ] = useState( [] );
    const [ chartTitlesRef, setChartTitlesRef ] = useState();
    const [ chartContainer, setChartContainer ] = useState( {} );
    const exportRef = useRef();

    const saveChart = async ( ) => {

        const d = document.createElement( 'div' );

        const releaseTitle = chartTitle.replace( /<[^>]*>/g, '' );

        const downloadFileName = `LIFE Survey-${ releaseTitle }.png`;

        await exportElementAsImage( exportRef.current, d, downloadFileName, null, null, 'Labor, Income, Finances, and Expectations (LIFE) Survey' );

    };

    /* fetch all of the data */
    useEffect( () => {

        fetchAllLifeData().then( ( { filterOps, allData } ) => {

            const newFilterCatMap = {};

            for ( const category of filterOps ) {

                for ( const { value } of category[ 'options' ] ) {

                    newFilterCatMap[ value ] = category[ 'label' ];

                }

            }

            setAllChartData( allData );
            setFilterCategoryMap( newFilterCatMap );
            setEconVariableFilterOptions( filterOps );

            // set initial chart states
            const { value } = filterOps[ 0 ][ 'options' ][ 0 ];
            const econVariableSpec = allData[ newFilterCatMap[ value ] ][ 'economicVariables' ][ value ];

            setSelectedEconVariableFilter( { label: defaultEconomicVariable, value: defaultEconomicVariable } );
            setAvailableCheckboxFilters( econVariableSpec[ 'filters' ] );

        } );

        window.addEventListener( 'resize', handleResize );

        // Unmounting
        return ( _ ) => {

            window.removeEventListener( 'resize', handleResize );

        };

    }, [] );

    /* slim down data by econ variable (and toggler, if applicable), update the toggler being visible or not */
    useEffect( () => {

        if ( selectedEconVariableFilter && Object.keys( selectedEconVariableFilter ).length > 0 ) {

            const { economicVariables } = allChartData[ filterCategoryMap[ selectedEconVariableFilter[ 'value' ] ] ];

            let newSlimmedEconData = [];

            let newToggleVis = false;

            // base employ/not employed button showing if the data is there or not rather than the category of the variable
            if ( economicVariables[ selectedEconVariableFilter[ 'value' ] ][ 'data' ].filter( x => x.employed ).length > 0 ) {

                // if ( filterCategoryMap[selectedEconVariableFilter['value']] === 'Labor - Worrying About Work' && selectedEconVariableFilter['value'] !== 'EMP_CURR_rc' ) {

                newToggleVis = true;
                newSlimmedEconData = economicVariables[ selectedEconVariableFilter[ 'value' ] ][ 'data' ].filter( ( { employed } ) =>
                    ( !toggle && employed === 'Employed' ) || ( toggle && employed === 'Not Employed' ),
                );

            }
            else {

                newSlimmedEconData = economicVariables[ selectedEconVariableFilter[ 'value' ] ][ 'data' ];

            }

            setToggleVisible( newToggleVis );
            setChartTitle( economicVariables[ selectedEconVariableFilter[ 'value' ] ][ 'chartTitle' ] );
            setSlimmedEconData( newSlimmedEconData );

        }

    }, [ selectedEconVariableFilter, toggle ] );

    /* update available checkboxes based on econ data (slimmed by econ var + toggler) + new demographic filter */
    useEffect( () => {

        // update the possible checkbox filters based on the data
        if ( Object.keys( allChartData ).length > 0 ) {

            const demoFilteredData = [];

            if ( selectedDemographicFilter[ 'value' ] === 'All Respondents' ) {

                demoFilteredData.push( ...slimmedEconData.filter( d => d[ 'All Respondents' ] === 'All Respondents' ) );

            }
            else {

                demoFilteredData.push( ...slimmedEconData.filter( d => d[ selectedDemographicFilter[ 'value' ] ] ) );

            }

            // get the checkbox filters that are available to us
            const availableFilters = {};

            for ( const d of demoFilteredData ) {

                for ( const key in d ) {

                    const found = multiCheckboxDemographicFilterOptions.find( ( { label } ) => key === label );

                    if ( found && d[ key ] ) {

                        if ( key in availableFilters ) {

                            if ( !availableFilters[ key ].includes( d[ key ] ) ) {

                                availableFilters[ key ].push( d[ key ] );

                            }

                        }
                        else {

                            availableFilters[ key ] = [ d[ key ] ];

                        }

                    }

                }

            }

            setSlimmedEconDemographicData( demoFilteredData );
            setAvailableCheckboxFilters( availableFilters );

        }

    }, [ slimmedEconData, selectedDemographicFilter ] );

    /* update what checkboxes are available if one checkbox has been selected */
    useEffect( () => {

        let newSlimmedCBData = [];

        if ( selectedCheckboxFilters.length === 0 ) newSlimmedCBData = slimmedEconDemographicData;
        else {

            newSlimmedCBData = slimmedEconDemographicData
                .filter( d => selectedCheckboxFilters.every( ( { category, subOption } ) => d[ category ] === subOption ) );

        }

        const availableFilters = {};

        for ( const d of newSlimmedCBData ) {

            for ( const key in d ) {

                const found = multiCheckboxDemographicFilterOptions.find( ( { label } ) => key === label );

                if ( found && d[ key ] ) {

                    if ( key in availableFilters ) {

                        if ( !availableFilters[ key ].includes( d[ key ] ) ) {

                            if ( d.value !== undefined ) {

                                availableFilters[ key ].push( d[ key ] );

                            }

                        }

                    }
                    else {

                        if ( d.value !== undefined ) {

                            availableFilters[ key ] = [ d[ key ] ];

                        }

                    }

                }

            }

            setAvailableCheckboxFilters( availableFilters );
            setSlimmedCheckboxFilteredData( newSlimmedCBData );

        }
        // setSlimmedCheckboxFilteredData

    }, [ slimmedEconDemographicData, selectedCheckboxFilters ] );

    /* all filters are applied, actually filter down the dataset to what should be rendered */
    useEffect( () => {

        let newSubtitle = `${ selectedDemographicFilter[ 'value' ] === 'All Respondents' ? 'All Respondents' : `By ${ selectedDemographicFilter[ 'value' ] }` }`;

        if ( selectedCheckboxFilters.length > 0 ) {

            newSubtitle += `: ${ selectedCheckboxFilters.map( ( { subOption } ) => subOption ).join( ', ' ) }`;

        }

        if ( toggleVisible ) {

            if ( !toggle ) newSubtitle += ' - Employed';
            else newSubtitle += ' - Not Employed';

        }
        if ( selectedEconVariableFilter && Object.keys( selectedEconVariableFilter ).length > 0 ) {

            let userSelectionsLim = 0;
            const econData = allChartData[ filterCategoryMap[ selectedEconVariableFilter[ 'value' ] ] ][ 'economicVariables' ][ selectedEconVariableFilter[ 'value' ] ][ 'data' ];
            // filter by the toggler
            const toggleEconData = [];

            if ( toggleVisible ) {

                toggleEconData.push( ...econData.filter( ( { employed } ) =>
                    ( !toggle && employed === 'Employed' ) || ( toggle && employed === 'Not Employed' ),
                ) );

            }
            else {

                toggleEconData.push( ...econData.filter( ( { employed } ) => !employed ) );

            }

            // filter by demographics
            const demoToggleEconData = [];

            if ( selectedDemographicFilter[ 'value' ] === 'All Respondents' ) {

                demoToggleEconData.push( ...toggleEconData.filter( t => t[ 'All Respondents' ] === 'All Respondents' ) );

            }
            else {

                userSelectionsLim += 1;
                demoToggleEconData.push( ...toggleEconData.filter( t => t[ selectedDemographicFilter[ 'value' ] ] ) );

            }

            const checkboxDemoToggleEconData = [];

            if ( selectedCheckboxFilters.length === 0 ) {

                checkboxDemoToggleEconData.push( ...demoToggleEconData.filter( d => d[ 'user_selections' ] === userSelectionsLim ) );

            }
            else {

                userSelectionsLim += selectedCheckboxFilters.length;
                checkboxDemoToggleEconData.push( ...demoToggleEconData.filter( ( d ) => {

                    return selectedCheckboxFilters.every( ( { category, subOption } ) => d[ category ] === subOption ) && d[ 'user_selections' ] === userSelectionsLim;

                } ) );

            }

            let allResData = [];

            // along with the filtered datasets, we are always including the All Respondents data

            if ( selectedDemographicFilter[ 'value' ] !== 'All Respondents' ) {

                allResData = econData.filter( ( e ) => {

                    return (
                        e[ 'All Respondents' ] === 'All Respondents'
                        && e[ 'user_selections' ] === selectedCheckboxFilters.length
                        && selectedCheckboxFilters.every( ( { category, subOption } ) => e[ category ] === subOption )
                    );

                } );

                // employ/not employed does not affect n_user_selections, so filter after if applicable
                // base employ/not employed button showing if the data is there or not rather than the category of the variable
                if ( allResData.filter( x => x.employed ).length > 0 ) { // if ( filterCategoryMap[selectedEconVariableFilter['value']] === 'Labor - Worrying About Work' ) {

                    allResData = allResData.filter( ( e ) => {

                        return ( !toggle && e[ 'employed' ] === 'Employed' ) || ( toggle && e[ 'employed' ] === 'Not Employed' );

                    } );

                }

            }

            extractData( checkboxDemoToggleEconData, allResData );

        }

        setActiveChartSubtitle( newSubtitle );

    }, [ slimmedCheckboxFilteredData ] );

    useEffect( () => {

        if ( formattedDatasets.length > 0 ) {

            const { rows } = formattedDatasets[ 0 ];

            setLegendLabels( rows.map( ( { name } ) => name ) );
            setRenderChart( true );

        }

    }, [ formattedDatasets ] );

    useEffect( () => {

        if ( chartTitlesRef && chartTitlesRef.current ) {

            setTitleHeights( chartTitlesRef.offsetHeight );

        }

    }, [ chartTitlesRef ] );

    useEffect( () => {

        if ( toggleVisible && toggle ) {

            setDynamicDisclaimer( {
                notes: `${ disclaimer[ 'notes' ] } Not employed only includes respondents under 55 years old.`,
                sources: disclaimer[ 'sources' ],
            } );

        }
        else {

            setDynamicDisclaimer( disclaimer );

        }

    }, [ toggle, toggleVisible ] );

    useEffect( () => {

        if ( exportRef.current !== undefined ) {

            setChartContainer( {
                left: exportRef.current.offsetLeft,
                right: exportRef.current.offsetLeft,
            } );

        }

    }, [ width ] );

    const fetchAllLifeData = async () => {

        const allData = {};

        for ( let i = 1; i < numFilesToFetch; i++ ) {

            const res = await fetch( `${ endpoint }/life_economic_variable_${ i }.json` );
            const lifeVariableData = await res.json();

            if ( lifeVariableData && lifeVariableData.length > 0 ) {

                // each dataset is exactly one economic variable over shared categories + subsets of data
                const { Category_Name, Category_Order, Chart_Title, Economic_Variable, Economic_Variable_Order } = lifeVariableData[ 0 ];

                if ( !( Category_Name in allData ) ) {

                    allData[ Category_Name ] = {
                        order: Category_Order,
                        economicVariables: {},
                    };

                }

                const curEconVariableFilters = {
                    'All Respondents': [],
                    'Income': [],
                    'Gender': [],
                    'Race/Ethnicity': [],
                    'Age': [],
                };
                const curEconVariableData = [];

                allData[ Category_Name ][ 'economicVariables' ][ Economic_Variable ] = {
                    chartTitle: Chart_Title,
                    order: Economic_Variable_Order,
                };
                lifeVariableData.forEach( ( {
                    Quarter_Date,
                    Quarter_Text,
                    n_responses,
                    n_user_selections,
                    All_Respondents,
                    Employed_Text,
                    Gender_Text,
                    Income_Text,
                    Race_Text,
                    Age_Text,
                    value,
                } ) => {

                    if ( All_Respondents && !curEconVariableFilters[ 'All Respondents' ].includes( All_Respondents ) ) {

                        curEconVariableFilters[ 'All Respondents' ].push( All_Respondents );

                    }

                    if ( Gender_Text && !curEconVariableFilters[ 'Gender' ].includes( Gender_Text ) ) {

                        curEconVariableFilters[ 'Gender' ].push( Gender_Text );

                    }

                    if ( Income_Text && !curEconVariableFilters[ 'Income' ].includes( Income_Text ) ) {

                        curEconVariableFilters[ 'Income' ].push( Income_Text );

                    }

                    if ( Race_Text && !curEconVariableFilters[ 'Race/Ethnicity' ].includes( Race_Text ) ) {

                        curEconVariableFilters[ 'Race/Ethnicity' ].push( Race_Text );

                    }

                    if ( Age_Text && !curEconVariableFilters[ 'Age' ].includes( Age_Text ) ) {

                        curEconVariableFilters[ 'Age' ].push( Age_Text );

                    }

                    curEconVariableData.push( {
                        'date': Quarter_Date,
                        'quarter': `${ Quarter_Text.split( '-' )[ 0 ] } ${ Quarter_Date.split( '-' )[ 0 ] }`,
                        'responses': n_responses,
                        'user_selections': n_user_selections,
                        'employed': Employed_Text,
                        'All Respondents': All_Respondents,
                        'Race/Ethnicity': Race_Text,
                        'Gender': Gender_Text,
                        'Income': Income_Text,
                        'Age': Age_Text,
                        value,
                    } );

                } );
                allData[ Category_Name ][ 'economicVariables' ][ Economic_Variable ][ 'data' ] = curEconVariableData;
                allData[ Category_Name ][ 'economicVariables' ][ Economic_Variable ][ 'filters' ] = curEconVariableFilters;

            }

        }

        const filterOps = [];

        for ( const c in allData ) {

            const catOption = { label: c, options: [] };

            for ( const econVar in allData[ c ][ 'economicVariables' ] ) {

                catOption[ 'options' ].push( { label: econVar, value: econVar, order: econVar[ 'order' ] } );

            }
            filterOps.push( catOption );

        }

        // sort the main categories, as well as the economic variable options within them
        filterOps.sort( ( a, b ) => allData[ a[ 'label' ] ][ 'order' ] - allData[ b[ 'label' ] ][ 'order' ] );
        filterOps.forEach( f => f[ 'options' ].sort( ( a, b ) =>
            allData[ f[ 'label' ] ][ 'economicVariables' ][ a[ 'label' ] ][ 'order' ] - allData[ f[ 'label' ] ][ 'economicVariables' ][ b[ 'label' ] ][ 'order' ] ),
        );

        return { filterOps, allData };

    };

    /* Gets the width property based on widow size */
    const handleResize = () => {

        const currWidth = componentWrapper.offsetWidth - 72;

        if ( width === currWidth ) return;

        setWidth( currWidth );

    };

    const getViewports = () => {

        return {
            isSVP: globalVPS.current( 'small' ),
            isMVP: globalVPS.current( 'medium' ),
            isLVP: globalVPS.current( 'large' ),
            currentVP: globalVPS.current().name,
        };

    };

    const handleEconVariableSelection = useCallback( ( { value, label } ) => {

        const econVariableSpec = allChartData[ filterCategoryMap[ value ] ][ 'economicVariables' ][ value ];

        setSelectedEconVariableFilter( { label, value } );
        setAvailableCheckboxFilters( econVariableSpec[ 'filters' ] );

    } );

    const handleDemographicSelection = useCallback( ( { value, label } ) => {

        setSelectedDemographicFilter( { value, label } );

    } );

    const handleCheckboxFilterSelection = useCallback( ( [ option1 = null, option2 = null ] ) => {

        const newSelected = [];

        if ( option1 ) newSelected.push( option1 );
        if ( option2 ) newSelected.push( option2 );

        setSelectedCheckboxFilters( newSelected );

    } );

    const extractData = ( dataset, allRespondentsData ) => {

        const sortedDataset = dataset.toSorted( ( a, b ) => new Date( a[ 'date' ] ) - new Date( b[ 'date' ] ) );

        const found = multiCheckboxDemographicFilterOptions.find( ( { label } ) => label === selectedDemographicFilter[ 'value' ] );

        if ( found !== null && found !== undefined ) {

            const allDemoOptions = found[ 'options' ];

            const rowLabels = [ 'quarter' ];

            for ( const option of allDemoOptions ) {

                if ( sortedDataset.some( s => s[ selectedDemographicFilter[ 'value' ] ] === option[ 'value' ] ) && !rowLabels.includes( option ) ) {

                    rowLabels.push( option[ 'value' ] );

                }

            }

            const newAllQuarters = [];

            for ( const s of sortedDataset ) {

                if ( !newAllQuarters.includes( s[ 'quarter' ] ) ) newAllQuarters.push( s[ 'quarter' ] );

            }

            const chartData = newAllQuarters.map( ( q ) => {

                const quarterData = sortedDataset.filter( s => s[ 'quarter' ] === q );
                const d = [];

                for ( const label of rowLabels.slice( 1 ) ) {

                    const found = quarterData.find( qd => qd[ selectedDemographicFilter[ 'value' ] ] === label );

                    if ( found ) d.push( found[ 'value' ] );
                    else d.push( null );

                }

                return [ q, ...d ];

            } );

            const firstRowColorMap = {};

            const a = chartData.map( ( data, i ) => {

                const rowValues = data.slice( 1 );

                const chartObj = {
                    column: {
                        quarter: data[ 0 ],
                        value: rowValues,
                        index: i,
                    },
                    rows: [],
                };

                chartObj[ 'rows' ] = rowValues.map( ( val, j ) => {

                    const rowObj = {
                        name: rowLabels[ j + 1 ],
                        value: val,
                    };

                    return rowObj;

                } );

                if ( i === 0 ) {

                    // const sortedRowValues = chartObj['rows'].map( ( { value } ) => value ).toSorted( ( a, b ) => b - a );

                    // // if all row values are unique, order the colors based on descending value
                    // if ( sortedRowValues.filter( ( v, idx, self ) => self.indexOf( v ) === idx ).length === sortedRowValues.length ) {

                    //     for ( const rowData of chartObj['rows'] ) {

                    //         rowData['color'] = chartColors[sortedRowValues.indexOf( rowData['value'] )];
                    //         firstRowColorMap[rowData['name']] = rowData['color'];

                    //     }

                    // }
                    // else {

                    for ( const rowDataIdx in chartObj[ 'rows' ] ) {

                        chartObj[ 'rows' ][ rowDataIdx ][ 'color' ] = chartColors[ rowDataIdx ];
                        firstRowColorMap[ chartObj[ 'rows' ][ rowDataIdx ][ 'name' ] ] = chartColors[ rowDataIdx ];

                    }

                    // }

                }
                else {

                    for ( const rowData of chartObj[ 'rows' ] ) {

                        rowData[ 'color' ] = firstRowColorMap[ rowData[ 'name' ] ];

                    }

                }

                if ( allRespondentsData && allRespondentsData.length > 0 ) {

                    // tack on all respondents data on the end
                    const found = allRespondentsData.find( ( { quarter } ) => quarter === chartObj[ 'column' ][ 'quarter' ] );

                    if ( found ) {

                        chartObj[ 'column' ][ 'value' ].push( found[ 'value' ] );
                        chartObj[ 'rows' ].push( {
                            name: 'All Respondents',
                            color: '#D5D5D5',
                            value: found[ 'value' ],
                        } );

                    }

                }

                return chartObj;

            } );

            setAllQuarters( newAllQuarters );
            setFormattedDatasets( a );

        }

    };

    return (
        <div className="life-data-viz-container">
            <div className="explorer-rich-text-heading" dangerouslySetInnerHTML={ { __html: richTextHeading } }>
            </div>

            <div className="explorer-controls">
                <div id="econ-variable-selector" className="chart-selection left-group">
                    <div className="chart-select-label"> Select an Economic Variable </div>
                    <Select
                        value={ selectedEconVariableFilter }
                        options={ econVariableFilterOptions }
                        styles={ getSelectStyles( '100%', '50px', globalVPS.current().name ) }
                        onChange={ handleEconVariableSelection }
                    />
                </div>

                <div id="demographic-selector" className="chart-selection left-group">
                    <div className="chart-select-label"> Select a Demographic </div>
                    <Select
                        value={ selectedDemographicFilter }
                        options={ allDemographicFilterOptions }
                        styles={ getSelectStyles( '100%', '50px', globalVPS.current().name ) }
                        onChange={ handleDemographicSelection }
                    />
                </div>

                <div id="checkboxes-selector" className="chart-selection left-group">
                    <CheckboxFilters
                        options={ multiCheckboxDemographicFilterOptions }
                        enabledOptions={ availableCheckboxFilters }
                        selectedDemographic={ selectedDemographicFilter }
                        onChange={ handleCheckboxFilterSelection }
                    />
                </div>

                {
                    toggleVisible
                        ? (
                            <div id="employed-toggler" className="chart-selection left-group">
                                <Toggle
                                    active={ toggle }
                                    setActive={ setToggle }
                                    labels={ [ 'Employed', 'Not Employed' ] }
                                    subLabels={ [ 'All Ages', 'Working Age' ] }
                                />
                            </div>
                        )
                        : null

                }

                <div className={ `line-download ${ toggleVisible ? 'line-download-with-toggle-visible' : '' }` }>

                    <DownloadButton
                        saveChart={ saveChart }
                    />
                </div>

            </div>

            <div className="chart-selection-section">

                <div className="svg-wrapper" ref={ exportRef }>
                    {
                        renderChart
                            ? (
                                <SelectionContext.Provider value={ { selectionQuarters, setSelectionQuarters } }>
                                    <div ref={ setChartTitlesRef } className="svg-chart-titles">
                                        <h3 className="svg-chart-header">{ chartTitle }</h3>
                                        <div className="svg-chart-subheader">{ chartSubtitle }</div>
                                    </div>

                                    <Chart
                                        selectedData={ formattedDatasets }
                                        allQuarters={ allQuarters }
                                        width={ width }
                                        pageWrapper={ pageWrapper }
                                        componentWrapper={ componentWrapper }
                                        chart={ chart }
                                        chartSubtitle={ chartSubtitle }
                                        titleHeights={ titleHeights }
                                        vps={ getViewports() }
                                        selectedEconVariableFilter={ selectedEconVariableFilter }
                                        chartContainer={ chartContainer }
                                    />

                                    <Legend
                                        yValues={ formattedDatasets.map( ( { rows } ) => rows ) }
                                        legendLabels={ legendLabels }
                                    />

                                    <div>

                                        <RangeSlider
                                            allQuarters={ allQuarters }
                                            allChartData={ slimmedCheckboxFilteredData }
                                            currentChartData={ formattedDatasets }
                                            margin={ { ...chart.margin, top: 0 } }
                                            chartWidth={ width }
                                            height={ 40 }
                                        />

                                    </div>
                                    <Disclaimer disclaimer={ dynamicDisclaimer } />
                                </SelectionContext.Provider>
                            )
                            : null
                    }

                </div>

            </div>

        </div>
    );

};

selection.propTypes = {
    pageWrapper: object.isRequired,
    componentWrapper: object.isRequired,
    attributes: object,
};

export default selection;
